/**
 * src/pages/Login.jsx — sign in, and first-run studio registration.
 *
 * Registration lives on the same page rather than its own route because this
 * is a self-serve product: the person arriving here either has an account or
 * is about to create one, and a separate /register route is one more thing to
 * find.
 */
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../lib/auth.jsx';
import { ErrorBanner } from '../components/ui.jsx';

export default function Login() {
  const { artist, loading, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({
    email: '',
    password: '',
    studioName: '',
    slug: '',
    name: '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Already signed in: go straight where they were headed.
  if (!loading && artist) {
    return <Navigate to={location.state?.from ?? '/dashboard'} replace />;
  }

  /**
   * Updates one form field.
   *
   * @param {string} key
   * @returns {(e: React.ChangeEvent<HTMLInputElement>) => void}
   */
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  /**
   * Suggests a URL slug from the studio name as the owner types it, so they
   * rarely have to think about the field at all.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onStudioName = (e) => {
    const studioName = e.target.value;
    setForm((f) => ({
      ...f,
      studioName,
      slug: slugify(studioName),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        await register({
          studioName: form.studioName,
          slug: form.slug,
          email: form.email,
          password: form.password,
          name: form.name || form.studioName,
        });
      }
      navigate(location.state?.from ?? '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="public-page" style={{ maxWidth: 420 }}>
      <div className="public-header">
        <h1>Studio</h1>
        <p className="muted small">
          {mode === 'login' ? 'Sign in to your studio.' : 'Set up your studio in one step.'}
        </p>
      </div>

      <form className="card stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>

        {mode === 'register' ? (
          <>
            <div>
              <label htmlFor="studioName">Studio name</label>
              <input
                id="studioName"
                value={form.studioName}
                onChange={onStudioName}
                required
                autoComplete="organization"
              />
            </div>
            <div>
              <label htmlFor="slug">Booking URL</label>
              <input id="slug" value={form.slug} onChange={set('slug')} required />
              <p className="muted small" style={{ marginTop: 4 }}>
                Clients will book at /book/{form.slug || 'your-studio'}
              </p>
            </div>
            <div>
              <label htmlFor="name">Your name</label>
              <input id="name" value={form.name} onChange={set('name')} autoComplete="name" />
            </div>
          </>
        ) : null}

        <div>
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={form.email}
            onChange={set('email')}
            required
            autoComplete="email"
          />
        </div>
        <div>
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            value={form.password}
            onChange={set('password')}
            required
            minLength={8}
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create studio'}
        </button>

        <button
          type="button"
          className="btn"
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError(null);
          }}
        >
          {mode === 'login' ? 'Set up a new studio' : 'I already have an account'}
        </button>
      </form>
    </div>
  );
}

/**
 * Turns a studio name into a URL-safe slug matching the API's validation.
 *
 * @param {string} value
 * @returns {string} e.g. "Ink by Maya" → "ink-by-maya".
 */
function slugify(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
